import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants";
import Typography from "../../../Utility/Typography";
import SystemListsNavigation from "./SystemListsNavigation";
import SaasModulesMasterPanel from "./SaasModulesMasterPanel";
import SystemListTagPanel from "./SystemListTagPanel";

const VALID_TABS = new Set(["saas_module", "client_tag", "vendor_tag", "language", "db_language"]);

function systemListsUrlTab(search) {
  const tab = new URLSearchParams(search ?? "").get("tab");
  if (tab && VALID_TABS.has(tab)) return tab;
  return "saas_module";
}

/**
 * Settings → System lists: segmented tabs (Work Items–style), default **saas_module**.
 *
 * Route access: any authenticated user (`ProtectedRoute` only). SaaS module **mutations** require
 * `SAAS_MODULE_ADD` / `SAAS_MODULE_EDIT` / `SAAS_MODULE_DELETE` in `SaasModulesMasterPanel`.
 * Client/vendor tag **mutations** require `SYSTEM_LIST_*` in `SystemListTagPanel` / `SystemListTagModal`.
 */
export default function SystemListsSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => systemListsUrlTab(location.search));

  useEffect(() => {
    setActiveTab(systemListsUrlTab(location.search));
  }, [location.search]);

  const handleTabChange = (tab) => {
    if (tab === "saas_module") {
      navigate(ROUTES.SETTINGS_SYSTEM_LISTS);
      return;
    }
    navigate(`${ROUTES.SETTINGS_SYSTEM_LISTS}?tab=${encodeURIComponent(tab)}`);
  };

  return (
    <div className="w-full">
      <Typography variant="h4" className="mb-2 text-theme-primary">
        System lists
      </Typography>
      <Typography variant="text2" className="mb-4 text-text-secondary">
        Manage global list configuration used across the application.
      </Typography>

      <SystemListsNavigation activeTab={activeTab} onTabChange={handleTabChange} />

      {activeTab === "saas_module" && <SaasModulesMasterPanel />}
      {activeTab === "client_tag" && (
        <SystemListTagPanel
          listType="CLIENT_TAG"
          panelTitle="Client tags"
          entityLabel="Client tag"
        />
      )}
      {activeTab === "vendor_tag" && (
        <SystemListTagPanel
          listType="VENDOR_TAG"
          panelTitle="Vendor tags"
          entityLabel="Vendor tag"
        />
      )}
      {activeTab === "language" && (
        <SystemListTagPanel
          listType="LANGUAGE"
          panelTitle="Script Languages"
          entityLabel="Script Language"
        />
      )}
      {activeTab === "db_language" && (
        <SystemListTagPanel
          listType="DB_LANGUAGE"
          panelTitle="Database Languages"
          entityLabel="Database Language"
        />
      )}
    </div>
  );
}
